/**
 * services/api.js
 * Centralized API client for Road Fund Dashboard
 * Base URL is read from REACT_APP_API_BASE (set in .env)
 */

import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:3000/api';

const http = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor: attach auth token if present ──────────────────────
http.interceptors.request.use((config) => {
  const token = localStorage.getItem('rf_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Response interceptor: normalize all responses ─────────────────────────
http.interceptors.response.use(
  (res) => res.data,
  (err) => {
    const message = err.response?.data?.message || err.message || 'Network error';
    console.error(`[API] ${err.config?.method?.toUpperCase()} ${err.config?.url} → ${message}`);
    return Promise.reject(new Error(message));
  }
);

// ── Helper: wrap any call so it always returns { success, data } ───────────
// Backend already returns { success, data } — interceptor gives us res.data
// so fn() returns the backend envelope directly. We just pass it through.
const safe = async (fn) => {
  try {
    const res = await fn();
    // res is already { success, data, ... } from backend
    if (res && typeof res === 'object' && 'success' in res) return res;
    return { success: true, data: res };
  } catch (e) {
    console.warn('[API] safe error:', e.message);
    return { success: false, data: null, error: e.message };
  }
};

// ══════════════════════════════════════════════════════════════════════════════
// DASHBOARD endpoints
// ══════════════════════════════════════════════════════════════════════════════
const dashboardGetKpi = (params = {}) =>
  safe(() => http.get('/dashboard/kpi', { params }));

const dashboardGetTrend = (params = {}) =>
  safe(() => http.get('/dashboard/daily-trend', { params }));

const dashboardGetRevenueBySource = (params = {}) =>
  safe(() => http.get('/dashboard/revenue-by-source', { params }));

const dashboardGetRecentTransactions = (params = {}) =>
  safe(() => http.get('/dashboard/recent-transactions', { params }));

const dashboardGetLocations = (params = {}) =>
  safe(() => http.get('/dashboard/locations', { params }));

const dashboardGetModules = (params = {}) =>
  safe(() => http.get('/dashboard/modules', { params }));

const dashboardGetHourlyDistribution = (params = {}) =>
  safe(() => http.get('/dashboard/hourly-distribution', { params }));

const dashboardGetTopStations = (params = {}) =>
  safe(() => http.get('/dashboard/top-stations', { params }));

const dashboardGetTrendByModule = (params = {}) =>
  safe(() => http.get('/dashboard/trend-by-module', { params }));

const dashboardGetVehicleAnalytics = (params = {}) =>
  safe(() => http.get('/dashboard/vehicle-analytics', { params }));

// ══════════════════════════════════════════════════════════════════════════════
// REPORTS endpoints
// ══════════════════════════════════════════════════════════════════════════════
const reportsGet = (params = {}) =>
  safe(() => http.get('/reports', { params }));

const reportsGetTransactions = (params = {}) =>
  safe(() => http.get('/reports/transactions', { params }));

const reportsGetRevenue = (params = {}) =>
  safe(() => http.get('/reports/revenue', { params }));

const reportsGenerateDaily = (data = {}) =>
  safe(() => http.post('/reports/generate/daily', data));

const reportsGenerateMonthly = (data = {}) =>
  safe(() => http.post('/reports/generate/monthly', data));

const reportsGetExportUrl = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return `${BASE_URL}/reports/export/csv?${query}`;
};

const reportsExportExcel = (params = {}) =>
  // Backend uses: POST /api/reports/export with exportType + date range
  safe(() =>
    http.post('/reports/export', {
      ...params,
      exportType: params.exportType || 'EXCEL',
      format: params.format || 'EXCEL',
    })
  );

const reportsExportPdf = (params = {}) =>
  // Backend uses: POST /api/reports/export with exportType + date range
  safe(() =>
    http.post('/reports/export', {
      ...params,
      exportType: params.exportType || 'PDF',
      format: params.format || 'PDF',
    })
  );

// ══════════════════════════════════════════════════════════════════════════════
// SETTINGS endpoints
// ══════════════════════════════════════════════════════════════════════════════
const settingsGet = () =>
  safe(() => http.get('/settings'));

const settingsSave = (data) =>
  // Backend exposes setting updates at: PUT /api/settings/:key
  safe(() =>
    http.put(`/settings/${data?.key}`, {
      value: data?.value,
      updatedBy: data?.updatedBy,
    })
  );

const settingsGetApiStatus = () =>
  safe(() => http.get('/settings/api-status'));

const settingsCheckApiStatus = (serviceName) =>
  safe(() => http.post(`/settings/api-status/${serviceName}/check`));

const settingsGetAuditLogs = (params = {}) =>
  safe(() => http.get('/settings/audit-logs', { params }));

// ══════════════════════════════════════════════════════════════════════════════
// USERS endpoints
// ══════════════════════════════════════════════════════════════════════════════
const usersGetAll = (params = {}) =>
  // Backend exposes users at: GET /api/users
  safe(() => http.get('/users', { params }));

const usersCreate = (data) =>
  safe(() => http.post('/users', data));

const usersUpdate = (id, data) =>
  safe(() => http.put(`/users/${id}`, data));

const usersDelete = (id) =>
  safe(() => http.delete(`/users/${id}`));

// ══════════════════════════════════════════════════════════════════════════════
// STATIONS endpoints
// ══════════════════════════════════════════════════════════════════════════════
const stationsGetAll = () =>
  safe(() => http.get('/stations'));

const stationsGetById = (id) =>
  safe(() => http.get(`/stations/${id}`));

const stationsCreate = (data) =>
  safe(() => http.post('/stations', data));

const stationsUpdate = (id, data) =>
  safe(() => http.put(`/stations/${id}`, data));

const stationsDelete = (id) =>
  safe(() => http.delete(`/stations/${id}`));

// ══════════════════════════════════════════════════════════════════════════════
// MODULES — Toll, Bridge, WIM, Auction
// ══════════════════════════════════════════════════════════════════════════════
const modulesGetKpi = (module) =>
  safe(() => http.get(`/modules/${module}/kpi`));

const modulesGetStations = (module, params = {}) =>
  // Backend exposes module stations at: GET /api/modules/:type
  safe(() => http.get(`/modules/${module}`, { params }));

const modulesGetStationDetail = (module, stationId) =>
  safe(() => http.get(`/modules/${module}/stations/${stationId}`));

const modulesGetTransactions = (module, params = {}) =>
  safe(() => http.get(`/modules/${module}/transactions`, { params }));

// ══════════════════════════════════════════════════════════════════════════════
// TOPUP OBU/UHF endpoints
// ══════════════════════════════════════════════════════════════════════════════
const topupGetSummary = (params = {}) =>
  safe(() => http.get('/topup/summary', { params }));

const topupGetTrend = (params = {}) =>
  safe(() => http.get('/topup/trend', { params }));

const topupGetList = (params = {}) =>
  safe(() => http.get('/topup/list', { params }));

const topupGetDevices = (params = {}) =>
  safe(() => http.get('/topup/devices', { params }));

// ══════════════════════════════════════════════════════════════════════════════
// AUTH (if needed)
// ══════════════════════════════════════════════════════════════════════════════
const authLogin = (credentials) =>
  safe(() => http.post('/auth/login', credentials));

const authLogout = () =>
  safe(() => http.post('/auth/logout'));

const authGetMe = () =>
  safe(() => http.get('/auth/me'));

// ══════════════════════════════════════════════════════════════════════════════
// Export all
// ══════════════════════════════════════════════════════════════════════════════
const apiClient = {
  // Dashboard
  dashboardGetKpi,
  dashboardGetTrend,
  dashboardGetRevenueBySource,
  dashboardGetRecentTransactions,
  dashboardGetLocations,
  dashboardGetModules,
  dashboardGetHourlyDistribution,
  dashboardGetTopStations,
  dashboardGetTrendByModule,
  dashboardGetVehicleAnalytics,

  // Reports
  reportsGet,
  reportsGetTransactions,
  reportsGetRevenue,
  reportsGetExportUrl,
  reportsExportExcel,
  reportsExportPdf,
  reportsGenerateDaily,
  reportsGenerateMonthly,

  // Settings
  settingsGet,
  settingsSave,
  settingsGetApiStatus,
  settingsCheckApiStatus,
  settingsGetAuditLogs,

  // Users
  usersGetAll,
  usersCreate,
  usersUpdate,
  usersDelete,

  // Modules
  modulesGetKpi,
  modulesGetStations,
  modulesGetStationDetail,
  modulesGetTransactions,

  // Stations
  stationsGetAll,
  stationsGetById,
  stationsCreate,
  stationsUpdate,
  stationsDelete,

  // Topup OBU/UHF
  topupGetSummary,
  topupGetTrend,
  topupGetList,
  topupGetDevices,

  // Auth
  authLogin,
  authLogout,
  authGetMe,
};

export default apiClient;
